import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ErrorResponse } from '@sparkai/utils'

import { apiClient } from '../../apiClient'

type AvailabilityWindowBase = {
  programId: string
  days: number[]
  engagementCount: number
  timeBlockInMinutes: number
  startHour: string
  endHour: string
}

type AvailabilityWindowResponse = AvailabilityWindowBase & {
  availabilityWindowId: string
}

type AvailabilityWindowDeleteRequest = {
  availabilityWindowId: string
  programId: string
}

type AvailabilityWindowPutRequest = AvailabilityWindowBase & {
  availabilityWindowId: string
}

export const PROGRAM_AVAILABILITY_WINDOW_KEY = 'program-setting-availability_window'

export function useGetById(programId?: string) {
  return useQuery({
    queryKey: [PROGRAM_AVAILABILITY_WINDOW_KEY, programId],
    queryFn: async () => {
      const response = await apiClient.get<AvailabilityWindowResponse[]>(
        `/int/v2/program/availability_window?programId=${programId}`
      )
      return response.data
    },
    enabled: Boolean(programId),
  })
}

export function useDelete(onSuccess: () => void) {
  const queryClient = useQueryClient()

  return useMutation<string, ErrorResponse, AvailabilityWindowDeleteRequest>({
    mutationFn: async (values: AvailabilityWindowDeleteRequest) => {
      await apiClient.delete<AvailabilityWindowDeleteRequest>(
        `int/v2/program/availability_window/${values.availabilityWindowId}`
      )
      return values.programId
    },
    onSuccess: (programId) => {
      queryClient.invalidateQueries({ queryKey: [PROGRAM_AVAILABILITY_WINDOW_KEY, programId] })
      onSuccess()
    },
  })
}

export function useCreate(onSuccess: () => void) {
  const queryClient = useQueryClient()

  return useMutation<AvailabilityWindowResponse, ErrorResponse, AvailabilityWindowBase>({
    mutationFn: async (values: AvailabilityWindowBase) => {
      const { data } = await apiClient.post<AvailabilityWindowResponse>(
        'int/v2/program/availability_window',
        values
      )
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [PROGRAM_AVAILABILITY_WINDOW_KEY, data.programId] })
      onSuccess()
    },
  })
}

export function useEdit(onSuccess: () => void) {
  const queryClient = useQueryClient()

  return useMutation<AvailabilityWindowResponse, ErrorResponse, AvailabilityWindowPutRequest>({
    mutationFn: async (values: AvailabilityWindowPutRequest) => {
      const { data } = await apiClient.put<AvailabilityWindowPutRequest>(
        `int/v2/program/availability_window/${values.availabilityWindowId}`,
        values
      )
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [PROGRAM_AVAILABILITY_WINDOW_KEY, data.programId] })
      onSuccess()
    },
  })
}
